import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, ExternalLink } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const begambleawareImg = '/images/begambleaware.png';
  const gamblingCommissionImg = '/images/gambling_commission_logo.png';
  const gamcareImg = '/images/gamcare.png';
  const gamstopImg = '/images/gamstop_logo.png';
  const ageRestrictionImg = '/images/18+.png';

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white" itemScope itemType="https://schema.org/Organization">
      <div className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Brand Column */}
          <div>
            <Link to="/" className="flex items-center space-x-2 mb-4" data-discover="true" itemProp="url">
              <img 
                src="/images/favicon.png" 
                alt="Crown Jackpot" 
                className="w-10 h-10 object-contain"
                itemProp="logo"
              />
              <span className="text-2xl font-bold" itemProp="name">Crown Jackpot</span>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed" itemProp="description">
              Your trusted guide to the best online casinos in the UK. Expert reviews, exclusive bonuses, and responsible gambling resources.
            </p>
            <p className="text-gray-400 text-sm leading-relaxed">
              <span itemProp="email">info@crown-jackpot.com</span>
            </p>
            <p className="text-gray-400 text-sm leading-relaxed">
              <span itemProp="telephone">+44 9296 526 586</span>
            </p>
            <div itemScope itemType="https://schema.org/PostalAddress" className="text-gray-400 text-sm leading-relaxed">
              <span itemProp="addressLocality">London</span>, <span itemProp="addressCountry">United Kingdom</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-lg mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-400 hover:text-green-400 transition-colors text-sm" data-discover="true">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/guides" className="text-gray-400 hover:text-green-400 transition-colors text-sm" data-discover="true">
                  Guides
                </Link>
              </li>
              <li>
                <Link to="/news" className="text-gray-400 hover:text-green-400 transition-colors text-sm" data-discover="true">
                  News
                </Link>
              </li>
              <li>
                <Link to="/reviews" className="text-gray-400 hover:text-green-400 transition-colors text-sm" data-discover="true">
                  Reviews
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-bold text-lg mb-4">Legal links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/privacy-policy" className="text-gray-400 hover:text-green-400 transition-colors text-sm" data-discover="true">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms-conditions" className="text-gray-400 hover:text-green-400 transition-colors text-sm" data-discover="true">
                  Terms & Conditions
                </Link>
              </li>
              <li>
                <Link to="/cookie-policy" className="text-gray-400 hover:text-green-400 transition-colors text-sm" data-discover="true">
                  Cookie Policy
                </Link>
              </li>
              <li>
                <Link to="/responsible-gaming" className="text-gray-400 hover:text-green-400 transition-colors text-sm" data-discover="true">
                  Responsible Gaming
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-bold text-lg mb-4">Responsible Gaming</h4>
            <div className="flex flex-wrap justify-center items-center gap-6 md:gap-8 mb-8">
              <div className="flex items-center">
                <a
                    href="https://www.begambleaware.org/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:opacity-80 transition-opacity"
                >
                  <img
                      className="h-[60px] w-auto object-contain"
                      alt="BeGambleAware - Responsible Gambling"
                      src={begambleawareImg}
                  />
                </a>
              </div>

              <div className="flex items-center">
                <a
                    href="https://www.gamblingcommission.gov.uk/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:opacity-80 transition-opacity"
                >
                  <img
                      className="h-[60px] w-auto object-contain"
                      alt="UK Gambling Commission"
                      src={gamblingCommissionImg}
                  />
                </a>
              </div>

              <div className="flex items-center">
                <a
                    href="https://www.gamcare.org.uk/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:opacity-80 transition-opacity"
                >
                  <img
                      className="h-[60px] w-auto object-contain"
                      alt="GamCare - Gambling Support"
                      src={gamcareImg}
                  />
                </a>
              </div>

              <div className="flex items-center">
                <a
                    href="https://www.gamstop.co.uk/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:opacity-80 transition-opacity"
                >
                  <img
                      className="h-[60px] w-auto object-contain"
                      alt="Gamstop - Self-Exclusion"
                      src={gamstopImg}
                  />
                </a>
              </div>

              <div className="flex items-center">
                <img
                    className="h-[60px] w-auto object-contain"
                    alt="18+ Age Restriction"
                    src={ageRestrictionImg}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Disclaimer and Copyright */}
        <div className="border-t border-gray-700 pt-8">
          <div className="mb-6">
            <p className="text-gray-400 text-xs leading-relaxed max-w-4xl">
              <strong className="text-gray-300">Disclaimer:</strong> Crown Jackpot operates as an independent review and comparison platform. 
              Certain links featured on our website are affiliate partnerships, which means we may earn a commission when you click through 
              or register through these links. This affiliate relationship does not influence our editorial integrity, review process, or 
              rating methodology. We maintain strict editorial standards and only recommend casinos that meet our rigorous criteria. 
              This website is intended for users 18 years of age and older. Please gamble responsibly. For support with problem gambling, 
              visit <a href="https://www.begambleaware.org" target="_blank" rel="noopener noreferrer" className="text-green-400 hover:underline">BeGambleAware.org</a>. 
              All terms and conditions apply to offers and promotions featured on this site.
            </p>
          </div>
          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-gray-500">
            <p itemProp="copyrightHolder" itemScope itemType="https://schema.org/Organization">
              <span itemProp="name">Crown Jackpot</span>. All rights reserved. © {currentYear}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

